/**
 * Curriculum model: the single source of truth for units, topics, and their
 * teaching order across the first nine weeks.
 *
 * The topic order mirrors the classroom sequence, which lets the app derive
 * "everything taught so far" from a single Current Topic pointer while still
 * allowing an explicit topicsTaught allow-list for finer control. Mixed review
 * (unit / first-9-weeks) draws only from taught topics so the app never tests
 * material the class has not reached.
 */

import type { SubskillId } from "./subskills";

export type UnitId = "unit1" | "unit2" | "unit3" | "unit4";

/** Stable topic identifiers - these also key the generator registry. */
export type TopicId =
  // Unit 1 - Solving Linear Equations
  | "two-step-fractions"
  | "multi-step-equations"
  | "variables-both-sides"
  | "proportions"
  | "absolute-value-equations"
  | "literal-equations"
  | "translating-equations"
  // Unit 2 - Solving Linear Inequalities
  | "writing-graphing-inequalities"
  | "multi-step-inequalities"
  | "compound-inequalities"
  | "absolute-value-inequalities"
  // Unit 3 - Functions and Relations
  | "discrete-continuous"
  | "domain-range"
  | "function-rules"
  | "vertical-line-test"
  | "function-notation"
  | "zeros"
  // Unit 4 - Graphing Linear Functions (Big Ideas Math Ch 3). First topic
  // implemented; the rest are enumerated in docs/curriculum-expansion-bigideas.md.
  | "graphing-slope-intercept";

export interface Unit {
  readonly id: UnitId;
  readonly name: string;
  readonly order: number;
}

export interface Topic {
  readonly id: TopicId;
  readonly unit: UnitId;
  readonly name: string;
  /** Position in the overall first-nine-weeks teaching sequence (1-based). */
  readonly order: number;
  /** Subskills this topic characteristically exercises. */
  readonly subskills: readonly SubskillId[];
  /** True if the topic renders or reads graphs (affects question UI). */
  readonly requiresGraphing: boolean;
}

export const UNITS: readonly Unit[] = [
  { id: "unit1", name: "Solving Linear Equations", order: 1 },
  { id: "unit2", name: "Solving Linear Inequalities", order: 2 },
  { id: "unit3", name: "Functions & Relations", order: 3 },
  { id: "unit4", name: "Graphing Linear Functions", order: 4 },
];

export const TOPICS: readonly Topic[] = [
  // Unit 1
  {
    id: "two-step-fractions",
    unit: "unit1",
    name: "Two-Step Equations with Fractions",
    order: 1,
    subskills: ["fractionArithmetic", "inverseOperations", "variableIsolation"],
    requiresGraphing: false,
  },
  {
    id: "multi-step-equations",
    unit: "unit1",
    name: "Multi-Step Equations",
    order: 2,
    subskills: [
      "distributive",
      "combiningLikeTerms",
      "negativeNumbers",
      "inverseOperations",
      "integerArithmetic",
    ],
    requiresGraphing: false,
  },
  {
    id: "variables-both-sides",
    unit: "unit1",
    name: "Variables on Both Sides",
    order: 3,
    subskills: [
      "variablesBothSides",
      "distributive",
      "combiningLikeTerms",
      "negativeNumbers",
      "inverseOperations",
      "variableIsolation",
    ],
    requiresGraphing: false,
  },
  {
    id: "proportions",
    unit: "unit1",
    name: "Proportions",
    order: 4,
    subskills: ["crossMultiplication", "fractionArithmetic", "inverseOperations"],
    requiresGraphing: false,
  },
  {
    id: "absolute-value-equations",
    unit: "unit1",
    name: "Absolute Value Equations",
    order: 5,
    subskills: ["absoluteValue", "inverseOperations", "negativeNumbers"],
    requiresGraphing: false,
  },
  {
    id: "literal-equations",
    unit: "unit1",
    name: "Literal Equations",
    order: 6,
    subskills: ["variableIsolation", "inverseOperations"],
    requiresGraphing: false,
  },
  {
    id: "translating-equations",
    unit: "unit1",
    name: "Translating Algebraic Equations",
    order: 7,
    subskills: ["substitution"],
    requiresGraphing: false,
  },
  // Unit 2
  {
    id: "writing-graphing-inequalities",
    unit: "unit2",
    name: "Writing & Graphing Inequalities",
    order: 8,
    subskills: ["graphInterpretation", "inverseOperations"],
    requiresGraphing: true,
  },
  {
    id: "multi-step-inequalities",
    unit: "unit2",
    name: "Multi-Step Inequalities",
    order: 9,
    subskills: [
      "inequalityReversal",
      "distributive",
      "combiningLikeTerms",
      "negativeNumbers",
      "inverseOperations",
    ],
    requiresGraphing: false,
  },
  {
    id: "compound-inequalities",
    unit: "unit2",
    name: "Compound Inequalities",
    order: 10,
    subskills: ["andOrReasoning", "inequalityReversal", "graphInterpretation"],
    requiresGraphing: true,
  },
  {
    id: "absolute-value-inequalities",
    unit: "unit2",
    name: "Absolute Value Inequalities",
    order: 11,
    subskills: ["absoluteValue", "andOrReasoning", "inequalityReversal"],
    requiresGraphing: true,
  },
  // Unit 3
  {
    id: "discrete-continuous",
    unit: "unit3",
    name: "Discrete & Continuous Relationships",
    order: 12,
    subskills: ["graphInterpretation", "coordinateInterpretation"],
    requiresGraphing: true,
  },
  {
    id: "domain-range",
    unit: "unit3",
    name: "Domain & Range",
    order: 13,
    subskills: ["domain", "range", "coordinateInterpretation", "graphInterpretation"],
    requiresGraphing: true,
  },
  {
    id: "function-rules",
    unit: "unit3",
    name: "Function Rules",
    order: 14,
    subskills: ["substitution", "functionIdentification"],
    requiresGraphing: false,
  },
  {
    id: "vertical-line-test",
    unit: "unit3",
    name: "Vertical Line Test",
    order: 15,
    subskills: ["functionIdentification", "graphInterpretation"],
    requiresGraphing: true,
  },
  {
    id: "function-notation",
    unit: "unit3",
    name: "Function Notation",
    order: 16,
    subskills: ["functionNotation", "substitution"],
    requiresGraphing: false,
  },
  {
    id: "zeros",
    unit: "unit3",
    name: "Zeros of Functions",
    order: 17,
    subskills: ["functionIdentification", "graphInterpretation", "coordinateInterpretation"],
    requiresGraphing: true,
  },
  // Unit 4 - Graphing Linear Functions
  {
    id: "graphing-slope-intercept",
    unit: "unit4",
    name: "Graphing in Slope-Intercept Form",
    order: 18,
    subskills: ["graphInterpretation", "coordinateInterpretation"],
    requiresGraphing: true,
  },
];

// --- Lookups -------------------------------------------------------------

const TOPIC_BY_ID = new Map<TopicId, Topic>(TOPICS.map((t) => [t.id, t]));
const UNIT_BY_ID = new Map<UnitId, Unit>(UNITS.map((u) => [u.id, u]));

export function getTopic(id: TopicId): Topic {
  const t = TOPIC_BY_ID.get(id);
  if (!t) throw new Error(`unknown topic: ${id}`);
  return t;
}

export function getUnit(id: UnitId): Unit {
  const u = UNIT_BY_ID.get(id);
  if (!u) throw new Error(`unknown unit: ${id}`);
  return u;
}

export function topicsInUnit(unit: UnitId): readonly Topic[] {
  return TOPICS.filter((t) => t.unit === unit).sort((a, b) => a.order - b.order);
}

export function isTopicId(x: string): x is TopicId {
  return TOPIC_BY_ID.has(x as TopicId);
}

/**
 * Everything taught up to and including the given topic, by teaching order.
 * Useful as a default topicsTaught allow-list derived from a single Current
 * Topic pointer.
 */
export function topicsUpToAndIncluding(id: TopicId): readonly Topic[] {
  const cutoff = getTopic(id).order;
  return TOPICS.filter((t) => t.order <= cutoff).sort(
    (a, b) => a.order - b.order,
  );
}

// --- Taught allow-list ---------------------------------------------------

/**
 * Which topics are available for practice/review. Explicit set of taught
 * topics; the current topic is always considered taught. Mixed review draws
 * only from this set.
 */
export class TaughtSet {
  private readonly taught: Set<TopicId>;

  constructor(taught: Iterable<TopicId> = []) {
    this.taught = new Set(taught);
  }

  /** Build from a Current Topic pointer, marking everything up to it taught. */
  static upTo(current: TopicId): TaughtSet {
    return new TaughtSet(topicsUpToAndIncluding(current).map((t) => t.id));
  }

  has(id: TopicId): boolean {
    return this.taught.has(id);
  }

  add(id: TopicId): void {
    this.taught.add(id);
  }

  remove(id: TopicId): void {
    this.taught.delete(id);
  }

  /** All taught topics, in teaching order. */
  list(): readonly Topic[] {
    return TOPICS.filter((t) => this.taught.has(t.id)).sort(
      (a, b) => a.order - b.order,
    );
  }

  /** Taught topics within a unit, in teaching order. */
  listInUnit(unit: UnitId): readonly Topic[] {
    return this.list().filter((t) => t.unit === unit);
  }

  ids(): readonly TopicId[] {
    return this.list().map((t) => t.id);
  }
}
